import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../auth';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../../shared/authHeaders';
import {
  normalizeModerationActions,
  normalizeModerationDashboard,
  type ModerationAction,
  type ModerationDashboard,
  type ModerationItemStatus,
  type ModerationPerson,
  type ModerationReport,
  type ModerationSafetySurvey,
  type ModerationStatusFilter,
} from './moderationApi';

const STATUS_FILTERS: Array<{ value: ModerationStatusFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

function formatDate(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusClasses(status: ModerationItemStatus) {
  if (status === 'open' || status === 'escalated') return 'bg-warn/20 text-warn';
  if (status === 'reviewed') return 'bg-ok/20 text-ok';
  if (status === 'dismissed') return 'bg-text-faint/20 text-text-dim';
  return 'bg-panel2 text-text-dim';
}

function personLabel(person: ModerationPerson | null) {
  return person?.displayName ?? 'Unknown profile';
}

async function readErrorMessage(response: Response, fallback: string) {
  const data = await response.json().catch(() => null);
  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
    return data.error;
  }
  return fallback;
}

export function AdminModerationPage() {
  const { getToken } = useAuth();
  const [dashboard, setDashboard] = useState<ModerationDashboard | null>(null);
  const [actions, setActions] = useState<ModerationAction[]>([]);
  const [statusFilter, setStatusFilter] = useState<ModerationStatusFilter>('open');
  const [isLoading, setIsLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ status: statusFilter });
      const response = await fetch(`/api/admin/moderation?${params}`, {
        headers: authHeaders(token),
      });
      if (response.status === 404 || response.status === 501) {
        setUnavailable(true);
        setDashboard(null);
        return;
      }
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in as an admin to view moderation');
        if (authMessage) throw new Error(authMessage);
        throw new Error(await readErrorMessage(response, 'Failed to load moderation dashboard'));
      }

      const data = await response.json().catch(() => null);
      const normalized = normalizeModerationDashboard(data);
      if (!normalized) {
        throw new Error('Moderation dashboard response was not valid');
      }

      setDashboard(normalized);
      setUnavailable(false);

      // Moderation-actions feed (bans, unbans, remote mutes) is additive: a
      // failure here should not hide the rest of the dashboard.
      try {
        const actionsResponse = await fetch('/api/admin/moderation/actions', {
          headers: authHeaders(token),
        });
        if (actionsResponse.ok) {
          setActions(normalizeModerationActions(await actionsResponse.json().catch(() => null)));
        }
      } catch {
        // Keep previously loaded actions.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load moderation dashboard');
      setDashboard(null);
    } finally {
      setIsLoading(false);
    }
  }, [getToken, statusFilter]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const updateModerationItem = async (
    kind: 'reports' | 'safety-surveys',
    id: string,
    status: 'reviewed' | 'dismissed'
  ) => {
    setUpdatingId(id);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/moderation/${kind}/${id}`, {
        method: 'PATCH',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in as an admin to update moderation');
        if (authMessage) throw new Error(authMessage);
        throw new Error(await readErrorMessage(response, 'Failed to update moderation item'));
      }

      await loadDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update moderation item');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-display font-medium text-text">Moderation</h1>
          <p className="text-sm text-text-dim mt-1">
            Review participant reports, model safety checks, and session reviews.
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as ModerationStatusFilter)}
          className="input w-full sm:w-auto"
        >
          {STATUS_FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>{filter.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-warn/30 bg-warn/10 px-4 py-3 text-sm text-warn">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-text-dim">Loading...</div>
        </div>
      ) : unavailable ? (
        <div className="rounded-xl border border-line bg-panel p-8 text-center">
          <h2 className="font-display text-xl font-normal text-text">Moderation API not enabled yet</h2>
          <p className="mt-2 text-sm text-text-dim">
            This page is ready for the approved moderation endpoints.
          </p>
        </div>
      ) : dashboard ? (
        <div className="space-y-6">
          <ModerationStats dashboard={dashboard} />

          <section>
            <h2 className="mb-3 font-display text-xl font-normal text-text">Moderation actions</h2>
            <ModerationActionsFeed actions={actions} />
          </section>

          <section>
            <h2 className="mb-3 font-display text-xl font-normal text-text">Reports</h2>
            <div className="space-y-3">
              {dashboard.reports.length === 0 ? (
                <EmptyPanel label="No reports match this filter." />
              ) : (
                dashboard.reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    updatingId={updatingId}
                    onUpdate={(status) => updateModerationItem('reports', report.id, status)}
                  />
                ))
              )}
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-xl font-normal text-text">Model safety checks</h2>
            <div className="space-y-3">
              {dashboard.safetySurveys.length === 0 ? (
                <EmptyPanel label="No safety checks match this filter." />
              ) : (
                dashboard.safetySurveys.map((survey) => (
                  <SafetySurveyCard
                    key={survey.id}
                    survey={survey}
                    updatingId={updatingId}
                    onUpdate={(status) => updateModerationItem('safety-surveys', survey.id, status)}
                  />
                ))
              )}
            </div>
          </section>

          <section>
            <h2 className="mb-3 font-display text-xl font-normal text-text">Recent reviews</h2>
            <div className="overflow-hidden rounded-xl border border-line bg-panel">
              {dashboard.reviews.length === 0 ? (
                <EmptyPanel label="No reviews match this filter." />
              ) : (
                <table className="w-full">
                  <thead className="border-b border-line bg-panel2">
                    <tr>
                      <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Reviewer</th>
                      <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Subject</th>
                      <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Score</th>
                      <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Session</th>
                      <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.reviews.map((review) => (
                      <tr key={review.id} className="border-b border-line last:border-0">
                        <td className="px-4 py-3 text-sm text-text">{personLabel(review.reviewer)}</td>
                        <td className="px-4 py-3 text-sm text-text">{personLabel(review.subject)}</td>
                        <td className="px-4 py-3 text-sm text-text">{review.score ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-text-dim">{review.roomName ?? review.roomCode ?? '-'}</td>
                        <td className="px-4 py-3">
                          <StatusPill status={review.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      ) : (
        <EmptyPanel label="No moderation data available." />
      )}
    </div>
  );
}

const ACTION_TYPE_LABELS: Record<string, string> = {
  'session.ban': 'Removed and blocked',
  'session.unban': 'Allowed back in',
  'session.mute': 'Remote muted',
};

function ModerationActionsFeed({ actions }: { actions: ModerationAction[] }) {
  if (actions.length === 0) {
    return <EmptyPanel label="No moderation actions recorded yet." />;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-panel">
      <table className="w-full">
        <thead className="border-b border-line bg-panel2">
          <tr>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">When</th>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Action</th>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Actor</th>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Target</th>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Session</th>
            <th className="px-4 py-3 text-left text-[10px] font-mono uppercase text-text-faint">Private reason</th>
          </tr>
        </thead>
        <tbody>
          {actions.map((action) => (
            <tr key={action.id} className="border-b border-line last:border-0">
              <td className="px-4 py-3 text-sm text-text-dim whitespace-nowrap">{formatDate(action.createdAt) ?? '-'}</td>
              <td className="px-4 py-3 text-sm text-text">
                {ACTION_TYPE_LABELS[action.actionType] ?? action.actionType}
              </td>
              <td className="px-4 py-3 text-sm text-text">{personLabel(action.actor)}</td>
              <td className="px-4 py-3 text-sm text-text">{personLabel(action.target)}</td>
              <td className="px-4 py-3 text-sm text-text-dim">{action.roomName ?? action.roomCode ?? '-'}</td>
              <td className="px-4 py-3 text-sm text-text-dim">{action.reason ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ModerationStats({ dashboard }: { dashboard: ModerationDashboard }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <StatCard label="Open reports" value={dashboard.totals.openReports} />
      <StatCard label="Unsafe safety checks" value={dashboard.totals.unsafeSurveys} />
      <StatCard label="Recent reviews" value={dashboard.totals.recentReviews} />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-line bg-panel p-4">
      <div className="text-[11px] uppercase text-text-dim">{label}</div>
      <div className="mt-2 font-display text-3xl font-light text-text">{value}</div>
    </div>
  );
}

function EmptyPanel({ label }: { label: string }) {
  return (
    <div className="rounded-xl border border-line bg-panel p-6 text-center text-sm text-text-dim">
      {label}
    </div>
  );
}

function StatusPill({ status }: { status: ModerationItemStatus }) {
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${statusClasses(status)}`}>
      {status}
    </span>
  );
}

function ReportCard({
  report,
  updatingId,
  onUpdate,
}: {
  report: ModerationReport;
  updatingId: string | null;
  onUpdate: (status: 'reviewed' | 'dismissed') => void;
}) {
  return (
    <article className="rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={report.status} />
            <span className="text-xs text-text-dim">{formatDate(report.createdAt)}</span>
            <span className="text-xs text-text-dim">{report.roomName ?? report.roomCode}</span>
          </div>
          <h3 className="mt-2 text-sm font-medium text-text">
            {personLabel(report.reporter)} reported {personLabel(report.reported)}
          </h3>
          <p className="mt-1 text-xs uppercase text-text-dim">{report.category}</p>
        </div>
        <ModerationActions
          disabled={updatingId === report.id}
          onReview={() => onUpdate('reviewed')}
          onDismiss={() => onUpdate('dismissed')}
        />
      </div>
      {report.body && (
        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-bg-secondary p-3 text-sm text-text-dim">
          {report.body}
        </p>
      )}
    </article>
  );
}

function SafetySurveyCard({
  survey,
  updatingId,
  onUpdate,
}: {
  survey: ModerationSafetySurvey;
  updatingId: string | null;
  onUpdate: (status: 'reviewed' | 'dismissed') => void;
}) {
  return (
    <article className="rounded-xl border border-line bg-panel p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={survey.status} />
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
              survey.feltSafe === false ? 'bg-warn/20 text-warn' : 'bg-ok/20 text-ok'
            }`}>
              {survey.feltSafe === false ? 'Did not feel safe' : survey.feltSafe === true ? 'Felt safe' : 'No answer'}
            </span>
            <span className="text-xs text-text-dim">{formatDate(survey.createdAt)}</span>
          </div>
          <h3 className="mt-2 text-sm font-medium text-text">
            {personLabel(survey.model)}
          </h3>
          <p className="mt-1 text-xs text-text-dim">{survey.roomName ?? survey.roomCode}</p>
        </div>
        <ModerationActions
          disabled={updatingId === survey.id}
          onReview={() => onUpdate('reviewed')}
          onDismiss={() => onUpdate('dismissed')}
        />
      </div>
      {survey.note && (
        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-bg-secondary p-3 text-sm text-text-dim">
          {survey.note}
        </p>
      )}
    </article>
  );
}

function ModerationActions({
  disabled,
  onReview,
  onDismiss,
}: {
  disabled: boolean;
  onReview: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button type="button" className="btn btn-sm" disabled={disabled} onClick={onDismiss}>
        Dismiss
      </button>
      <button type="button" className="btn btn-sm btn-accent" disabled={disabled} onClick={onReview}>
        Mark reviewed
      </button>
    </div>
  );
}
